import React, { useState, useEffect } from 'react';
import { Bell, Building2, Clock, UserCheck, Check, Trash2, X, Edit3 } from 'lucide-react';
import api from '../lib/api';

// ── PUNI PREGLED PODSJETNIKA (dijeljeni modal) ──
// Otvara se klikom na podsjetnik u: ReminderWidget listi, kartici Podsjetnici na
// profilu firme i CRM detalju. Pokazuje CIJELI tekst + firmu + vrijeme + zaduženje,
// s opcijama: Uredi (tekst/datum/sat/zaduži) · Završi · Obriši.
// Dizajn: Warm Executive (MASTER.md) — solid borderi, bez opacity na tekstu.

const T = {
  surface: '#FFFFFF', warm: '#FFF9F0', border: '#E8E5E0', page: '#FAFAF8',
  textPrimary: '#1C1917', textSecondary: '#57534E', textMuted: '#A8A29E',
  gold: '#C17F0A', green: '#15803D', red: '#DC2626',
};

const pad2 = (n) => String(n).padStart(2, '0');
function toLocalParts(remindAt) {
  const d = remindAt ? new Date(remindAt) : null;
  if (!d || isNaN(d)) return { date: '', time: '' };
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
}
function fmtFull(remindAt) {
  const d = remindAt ? new Date(remindAt) : null;
  if (!d || isNaN(d)) return '—';
  return d.toLocaleString('hr-HR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ReminderPreview({ reminder, teamMembers = [], onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(reminder?.text || '');
  const initial = toLocalParts(reminder?.remind_at);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time || '09:00');
  const [assignedTo, setAssignedTo] = useState(reminder?.assigned_to || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!reminder) return null;
  const id = reminder.reminder_id;
  const hasCompany = reminder.company_name && reminder.company_name !== '—';
  const overdue = reminder.remind_at && new Date(reminder.remind_at) < new Date() && !reminder.done;

  const memberName = (email) => {
    if (!email) return 'Meni (kreator)';
    const m = teamMembers.find(t => t.email === email);
    return m?.name || m?.full_name || email.split('@')[0];
  };

  const save = async () => {
    if (!text.trim() || !date || !time) { alert('Tekst, datum i sat su obavezni.'); return; }
    const remindAt = `${date}T${time}`;
    if (new Date(remindAt).getTime() <= Date.now()) { alert('Podsjetnik se može zadati samo za buduće vrijeme.'); return; }
    setSaving(true);
    try {
      await api.put(`/leads/reminders/${id}/edit`, { text: text.trim(), remind_at: remindAt, assigned_to: assignedTo || null });
      onChanged?.('edited', { ...reminder, text: text.trim(), remind_at: remindAt, assigned_to: assignedTo || null, done: false });
      onClose?.();
    } catch (e) {
      alert('Greška pri spremanju: ' + (e.response?.data?.detail || 'pokušaj ponovno'));
    }
    setSaving(false);
  };

  const markDone = async () => {
    try {
      await api.put(`/leads/reminders/${id}`);
      onChanged?.('done', { ...reminder, done: true });
      onClose?.();
    } catch (e) { alert('Greška.'); }
  };

  const del = async () => {
    if (!window.confirm('Obrisati ovaj podsjetnik?')) return;
    try {
      await api.delete(`/leads/reminders/${id}`);
      onChanged?.('deleted', reminder);
      onClose?.();
    } catch (e) { alert('Greška.'); }
  };

  const lbl = { fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
  const inp = { width: '100%', padding: '9px 11px', borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: T.surface, color: T.textPrimary };
  const btn = (bg, fg, border) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', background: bg, color: fg, border: border || 'none' });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(28,25,23,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: 12, boxShadow: '0 8px 24px rgba(120,100,60,0.12)', width: 480, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: T.warm, borderBottom: `1px solid ${T.border}` }}>
          <Bell size={17} style={{ color: T.gold }} />
          <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: T.textPrimary, letterSpacing: '-0.02em' }}>
            {editing ? 'Uredi podsjetnik' : 'Podsjetnik'}
          </div>
          {overdue && <span style={{ padding: '3px 9px', borderRadius: 6, fontSize: 10, fontWeight: 800, background: '#FEE2E2', color: '#991B1B', textTransform: 'uppercase' }}>Dospio</span>}
          {reminder.done && <span style={{ padding: '3px 9px', borderRadius: 6, fontSize: 10, fontWeight: 800, background: '#DCFCE7', color: '#166534', textTransform: 'uppercase' }}>Završen</span>}
          <button onClick={onClose} title="Zatvori (ESC)" style={{ display: 'inline-flex', padding: 6, borderRadius: 6, background: 'none', border: `1px solid ${T.border}`, cursor: 'pointer', color: T.textSecondary }}><X size={15} /></button>
        </div>

        <div style={{ overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Firma */}
          {hasCompany && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: T.warm, border: '1px solid #E8D4A8', borderRadius: 8 }}>
              <Building2 size={14} style={{ color: T.gold, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#78510A', lineHeight: 1.35 }}>{reminder.company_name}</span>
            </div>
          )}

          {/* Tekst */}
          <div>
            <div style={lbl}>Što treba napraviti</div>
            {editing ? (
              <textarea value={text} onChange={e => setText(e.target.value)} rows={4} style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }} autoFocus />
            ) : (
              <p style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.55, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{reminder.text || '(bez teksta)'}</p>
            )}
          </div>

          {/* Vrijeme */}
          <div>
            <div style={lbl}>Kada</div>
            {editing ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 8 }}>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
                <input type="time" step="300" value={time} onChange={e => setTime(e.target.value)} style={{ ...inp, textAlign: 'center', fontWeight: 700 }} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, color: overdue ? T.red : T.textPrimary }}>
                <Clock size={14} style={{ color: overdue ? T.red : T.gold }} /> {fmtFull(reminder.remind_at)}
              </div>
            )}
          </div>

          {/* Zaduženje */}
          <div>
            <div style={lbl}>Zadužen</div>
            {editing ? (
              <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Meni (kreator)</option>
                {teamMembers.map(m => <option key={m.email} value={m.email}>{m.name || m.full_name || m.email?.split('@')[0]}</option>)}
              </select>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: T.textSecondary }}>
                <UserCheck size={14} style={{ color: T.textMuted }} /> {memberName(reminder.assigned_to)}
                {reminder.created_by && <span style={{ fontSize: 11, color: T.textMuted }}>· kreirao {reminder.created_by.split('@')[0]}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Akcije */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '14px 20px 18px', borderTop: `1px solid ${T.border}` }}>
          {editing ? (
            <>
              <button onClick={save} disabled={saving} style={btn(T.gold, '#FFF')}><Check size={14} /> {saving ? 'Spremam…' : 'Spremi'}</button>
              <button onClick={() => setEditing(false)} style={btn(T.surface, T.textSecondary, `1px solid ${T.border}`)}>Odustani</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} style={btn(T.gold, '#FFF')}><Edit3 size={14} /> Uredi</button>
              {!reminder.done && <button onClick={markDone} style={btn(T.green, '#FFF')}><Check size={14} /> Završi</button>}
              <button onClick={del} style={btn('#FEF2F2', T.red, '1px solid #FECACA')}><Trash2 size={13} /> Obriši</button>
              <button onClick={onClose} style={{ ...btn(T.surface, T.textSecondary, `1px solid ${T.border}`), marginLeft: 'auto' }}>Zatvori</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
